import React, { useState, useEffect } from "react";
import jwtDecode from "jwt-decode";
import AdminService from "../services/AdminService";
import { toast } from "react-toastify";

const AdminProfile = () => {
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("profile");
    const [profile, setProfile] = useState({
        name: "",
        email: "",
        phone: "",
        imageUrl: "",
    });
    const [passwordForm, setPasswordForm] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    });
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
    const [activityLogs, setActivityLogs] = useState([]);
    const [profileImage, setProfileImage] = useState(null);

    useEffect(() => {
        loadProfile();
        loadLogs();
    }, []);

    const loadProfile = async () => {
        try {
            const response = await AdminService.getAdminProfile();
            if (response.data.data) {
                setProfile(response.data.data);
                setTwoFactorEnabled(response.data.data.twoFactorEnabled || false);
            }
        } catch {
            // Fallback: use token data
            const token = localStorage.getItem("user");
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    setProfile({
                        name: decoded.name || "Admin User",
                        email: decoded.email || "admin@caribbargains.com",
                        phone: decoded.phone || "+1 (868) 000-0000",
                        imageUrl: decoded.imageUrl || "",
                    });
                } catch (e) {
                    console.error(e);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const loadLogs = async () => {
        try {
            const response = await AdminService.getAdminLogs();
            setActivityLogs(response.data.data || []);
        } catch {
            setActivityLogs([
                { id: 1, action: "Logged in", ip: "192.168.1.1", date: "2024-12-20 10:30 AM" },
                { id: 2, action: "Updated system settings", ip: "192.168.1.1", date: "2024-12-20 11:00 AM" },
                { id: 3, action: "Blocked user Carlos Rivera", ip: "192.168.1.1", date: "2024-12-19 3:00 PM" },
                { id: 4, action: "Approved ad #4521", ip: "192.168.1.1", date: "2024-12-19 2:30 PM" },
                { id: 5, action: "Sent announcement", ip: "192.168.1.1", date: "2024-12-18 4:00 PM" },
            ]);
        }
    };

    const handleUpdateProfile = async () => {
        try {
            const formData = new FormData();
            formData.append("name", profile.name);
            formData.append("phone", profile.phone);
            if (profileImage) {
                formData.append("image", profileImage);
            }
            await AdminService.updateAdminProfile(formData);
            toast.success("Profile updated successfully");
        } catch {
            toast.info("Profile updated (API pending)");
        }
    };

    const handleChangePassword = async () => {
        if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
            toast.error("All password fields are required");
            return;
        }
        if (passwordForm.newPassword !== passwordForm.confirmPassword) {
            toast.error("New passwords do not match");
            return;
        }
        if (passwordForm.newPassword.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        try {
            await AdminService.changeAdminPassword({
                currentPassword: passwordForm.currentPassword,
                newPassword: passwordForm.newPassword,
            });
            toast.success("Password changed successfully");
            setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
        } catch {
            toast.info("Password change queued (API pending)");
        }
    };

    const handleToggle2FA = async () => {
        try {
            await AdminService.toggleTwoFactor({ enabled: !twoFactorEnabled });
            setTwoFactorEnabled(!twoFactorEnabled);
            toast.success(`Two-factor authentication ${!twoFactorEnabled ? "enabled" : "disabled"}`);
        } catch {
            setTwoFactorEnabled(!twoFactorEnabled);
            toast.info("2FA toggled (API pending)");
        }
    };

    if (loading) {
        return (
            <div className="admin-empty-state">
                <i className="fa fa-spinner fa-spin"></i>
                <p>Loading profile...</p>
            </div>
        );
    }

    return (
        <div>
            {/* Profile Header Card */}
            <div className="admin-card" style={{ overflow: "hidden" }}>
                <div className="admin-profile-header">
                    {profile.imageUrl ? (
                        <img src={profile.imageUrl} alt={profile.name} className="admin-profile-avatar" />
                    ) : (
                        <div className="admin-profile-avatar" style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "32px", color: "#fff" }}>
                            <i className="fa fa-user"></i>
                        </div>
                    )}
                    <div className="admin-profile-info">
                        <h3>{profile.name}</h3>
                        <p>{profile.email}</p>
                    </div>
                </div>
            </div>

            <div className="admin-tabs">
                <button className={`admin-tab ${activeTab === "profile" ? "active" : ""}`} onClick={() => setActiveTab("profile")}>
                    Profile
                </button>
                <button className={`admin-tab ${activeTab === "security" ? "active" : ""}`} onClick={() => setActiveTab("security")}>
                    Security
                </button>
                <button className={`admin-tab ${activeTab === "activity" ? "active" : ""}`} onClick={() => setActiveTab("activity")}>
                    Activity Log
                </button>
            </div>

            {activeTab === "profile" && (
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3>Edit Profile</h3>
                    </div>
                    <div className="admin-card-body">
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>Display Name</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    value={profile.name}
                                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Email (read-only)</label>
                                <input type="email" className="admin-form-control" value={profile.email} disabled style={{ background: "#f8f9fa" }} />
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-form-group">
                                <label>Phone</label>
                                <input
                                    type="text"
                                    className="admin-form-control"
                                    value={profile.phone}
                                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-group">
                                <label>Profile Picture</label>
                                <input
                                    type="file"
                                    className="admin-form-control"
                                    accept="image/*"
                                    onChange={(e) => setProfileImage(e.target.files[0])}
                                />
                            </div>
                        </div>
                        <div className="admin-form-actions">
                            <button className="admin-btn admin-btn-primary" onClick={handleUpdateProfile}>
                                <i className="fa fa-save"></i> Save Profile
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {activeTab === "security" && (
                <>
                    <div className="admin-card">
                        <div className="admin-card-header">
                            <h3>Change Password</h3>
                        </div>
                        <div className="admin-card-body">
                            <div className="admin-form-group">
                                <label>Current Password</label>
                                <input
                                    type="password"
                                    className="admin-form-control"
                                    placeholder="Enter current password"
                                    value={passwordForm.currentPassword}
                                    onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                                />
                            </div>
                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>New Password</label>
                                    <input
                                        type="password"
                                        className="admin-form-control"
                                        placeholder="At least 8 characters"
                                        value={passwordForm.newPassword}
                                        onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>Confirm New Password</label>
                                    <input
                                        type="password"
                                        className="admin-form-control"
                                        placeholder="Re-enter new password"
                                        value={passwordForm.confirmPassword}
                                        onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="admin-form-actions">
                                <button className="admin-btn admin-btn-primary" onClick={handleChangePassword}>
                                    <i className="fa fa-key"></i> Change Password
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* 
                    <div className="admin-card">
                        <div className="admin-card-header">
                            <h3>Two-Factor Authentication</h3>
                        </div>
                        <div className="admin-card-body">
                            <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
                                <label className="admin-toggle">
                                    <input type="checkbox" checked={twoFactorEnabled} onChange={handleToggle2FA} />
                                    <span className="admin-toggle-slider"></span>
                                </label>
                                <div>
                                    <div style={{ fontSize: "14px", fontWeight: 500 }}>
                                        {twoFactorEnabled ? "Two-factor authentication is enabled" : "Two-factor authentication is disabled"}
                                    </div>
                                    <div style={{ fontSize: "12px", color: "#6c757d" }}>
                                        Adds an extra layer of security to your admin account
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                     */}
                </>
            )}

            {activeTab === "activity" && (
                <div className="admin-card">
                    <div className="admin-card-header">
                        <h3>Recent Activity</h3>
                    </div>
                    <div className="admin-card-body" style={{ padding: 0 }}>
                        <table className="admin-table">
                            <thead>
                                <tr>
                                    <th>Action</th>
                                    <th>IP Address</th>
                                    <th>Date & Time</th>
                                </tr>
                            </thead>
                            <tbody>
                                {activityLogs.map((log) => (
                                    <tr key={log.id}>
                                        <td>{log.action}</td>
                                        <td>{log.ip}</td>
                                        <td>{log.date}</td>
                                    </tr>
                                ))}
                                {activityLogs.length === 0 && (
                                    <tr>
                                        <td colSpan="3" style={{ textAlign: "center", padding: "40px", color: "#6c757d" }}>
                                            No activity logs
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>


    );
};

export default AdminProfile;
